import { useEffect, useMemo, useState, useLayoutEffect } from "react";
import {
  getResearcherProfile,
  getResearcherAchievements,
  getResearcherActivity,
  listResearcherCertifications,
  getReportDetailsByRetestId
} from "../../utils/api/endpoints/researcher/profile";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";

const Tabs = ["Overview", "Achievements", "Certifications", "Activity"] as const;
type Tab = typeof Tabs[number];

const ProfilePage = () => {
  const { id: currentUserId } = useUserCredentials();
  const { userId: viewedUserIdParam } = useParams();
  const viewedUserId = viewedUserIdParam ? Number(viewedUserIdParam) : currentUserId;

  // Force scroll to top immediately on route/user change
  useLayoutEffect(() => {
    try {
      // Scroll the dashboard main container if present
      const el = document.getElementById('dashboard-main-scroll');
      if (el) el.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: "auto" });
      (document.scrollingElement || document.documentElement).scrollTop = 0;
      document.body.scrollTop = 0;
    } catch { }
  }, [viewedUserId]);
  const [tab, setTab] = useState<Tab>("Overview");
  const [profile, setProfile] = useState<any>(null);
  const [achievements, setAchievements] = useState<any[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [certs, setCerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAllPrograms, setShowAllPrograms] = useState(false);
  const [activityHasMore, setActivityHasMore] = useState<boolean>(false);
  const [activityLoadingMore, setActivityLoadingMore] = useState<boolean>(false);
  const [activityCollapsed, setActivityCollapsed] = useState<boolean>(true);
  const [retestReportDetails, setRetestReportDetails] = useState<Record<number, { title?: string; severity?: string }>>({});

  useEffect(() => {
    Promise.all([
      getResearcherProfile(viewedUserId),
      getResearcherAchievements(viewedUserId),
      getResearcherActivity(viewedUserId, { limit: 5, offset: 0 }),
      listResearcherCertifications(viewedUserId)
    ])
      .then(([p, a, act, c]) => {
        setProfile(p);
        setAchievements(a.achievements || []);
        setActivity(act.items || []);
        setActivityHasMore((act.items || []).length === 5);
        setActivityCollapsed(true);
        setCerts((c.certifications || []).slice(0, 3));
      })
      .finally(() => setLoading(false));
  }, [currentUserId, viewedUserId]);

  const handleLoadMoreActivity = async () => {
    if (!viewedUserId) return;
    // First expand the list if currently collapsed
    if (activityCollapsed) {
      setActivityCollapsed(false);
    }
    setActivityLoadingMore(true);
    try {
      const next = await getResearcherActivity(viewedUserId, { limit: 5, offset: activity.length });
      const items = next.items || [];
      if (items.length > 0) {
        setActivity(prev => [...prev, ...items]);
      }
      setActivityHasMore(items.length === 5);
    } finally {
      setActivityLoadingMore(false);
    }
  };

  // Fetch report details for any retest items so we can show report title and severity
  useEffect(() => {
    const retestIds: number[] = Array.from(
      new Set(
        (activity || [])
          .filter((it: any) => it.type === "retest" && typeof it.retest_id === "number")
          .map((it: any) => it.retest_id as number)
      )
    ).filter((rid) => retestReportDetails[rid] === undefined);

    if (retestIds.length === 0) return;

    let isCancelled = false;
    Promise.all(
      retestIds.map(async (rid) => {
        try {
          const data = await getReportDetailsByRetestId(rid);
          return { rid, title: data?.title, severity: data?.severity };
        } catch {
          return { rid, title: undefined, severity: undefined };
        }
      })
    ).then((entries) => {
      if (isCancelled) return;
      setRetestReportDetails((prev) => {
        const next = { ...prev } as Record<number, { title?: string; severity?: string }>;
        for (const e of entries) {
          next[e.rid] = { title: e.title, severity: e.severity };
        }
        return next;
      });
    });

    return () => {
      isCancelled = true;
    };
  }, [activity, retestReportDetails]);

  const getSeverityStyle = (sev?: string) => {
    const s = (sev || "").toString().toLowerCase();
    if (s === "critical") return { bg: "bg-red-100", text: "text-red-700", label: "Critical" };
    if (s === "high") return { bg: "bg-orange-100", text: "text-orange-700", label: "High" };
    if (s === "medium") return { bg: "bg-yellow-100", text: "text-yellow-700", label: "Medium" };
    if (s === "low") return { bg: "bg-green-100", text: "text-green-700", label: "Low" };
    if (s === "informational" || s === "info" || s === "none") return { bg: "bg-blue-100", text: "text-blue-700", label: "Informational" };
    return { bg: "bg-gray-100", text: "text-gray-700", label: s ? s.charAt(0).toUpperCase() + s.slice(1) : "None" };
  };

  const getRetestStatusStyle = (status?: string) => {
    const st = (status || "").toString().toLowerCase();
    if (st.includes("verified") || st.includes("approved")) return { bg: "bg-green-100", text: "text-green-700" };
    if (st.includes("failed") || st.includes("rejected")) return { bg: "bg-red-100", text: "text-red-700" };
    if (st.includes("process") || st.includes("pending") || st.includes("qa")) return { bg: "bg-blue-100", text: "text-blue-700" };
    return { bg: "bg-gray-100", text: "text-gray-700" };
  };

  // Top Skills label formatting (UI-only)
  const SKILL_LABEL_OVERRIDES: Record<string, string> = {
    ios: "iOS",
    api: "API",
    sql: "SQL",
    ml: "ML"
  };

  const formatSkillName = (raw: string): string => {
    const key = (raw || "").trim().toLowerCase();
    if (SKILL_LABEL_OVERRIDES[key]) return SKILL_LABEL_OVERRIDES[key];
    return (raw || "").replace(/\b\w+/g, w => w.charAt(0).toUpperCase() + w.slice(1));
  };

  // Program description formatting (strip HTML + truncate)
  const stripHtml = (html?: string) =>
    (html || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const formatProgramDescription = (raw?: string, max = 50) => {
    const text = stripHtml(raw);
    return text.length > max ? text.slice(0, max) + "..." : text;
  };

  return (
    <div className="bg-gray-100 min-h-screen p-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header */}
      {!loading && profile && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
              <p className="text-gray-600 mt-1">Manage your personal information and security researcher profile</p>
            </div>
            {viewedUserId === currentUserId && (
              <Link
                to="/dashboard/profile/edit/settings"
                className="bg-[#010D2C] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-[#020f35] transition-colors flex items-center gap-2"
              >
                <img src="/icons/edit.svg" alt="Edit" className="w-4 h-4" />
                Edit Profile
              </Link>
            )}
          </div>

          {/* Main White Container */}
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="flex">
              {/* Main Content - Left Side */}
              <div className="flex-1 p-6 space-y-6">
                {/* About Section */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">About</h2>
                  <p className="text-[#747e8b] font-normal leading-relaxed">
                    {profile.about?.bio || "This is an example bio please go to settings and change your about section from there"}
                  </p>
                </div>

                {/* Stats Boxes */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#F9FAFB] rounded-lg p-6 shadow-sm w-[95%] mx-auto">
                    <div className="flex items-center mb-3">
                      <img src="/icons/ranking.svg" alt="Ranking" className="w-5 h-5 mr-3 object-contain" />
                      <span className="text-sm font-medium text-[#010D2C]">Ranking</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">#{profile.stats?.rank ?? "-"}</div>
                    <div className="text-sm text-gray-500">CTB's Leaderboard</div>
                  </div>

                  <div className="bg-[#F9FAFB] rounded-lg p-6 shadow-sm w-[95%] mx-auto">
                    <div className="flex items-center mb-3">
                      <img src="/icons/reputation.svg" alt="Reputation" className="w-5 h-5 mr-3 object-contain" />
                      <span className="text-sm font-medium text-[#010D2C]">Reputation</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{(profile.stats?.reputation ?? 0).toLocaleString()}</div>
                    <div className="text-sm text-gray-500">Points Earned</div>
                  </div>

                  <div className="bg-[#F9FAFB] rounded-lg p-6 shadow-sm w-[95%] mx-auto">
                    <div className="flex items-center mb-3">
                      <img src="/icons/findings.svg" alt="Findings" className="w-5 h-5 mr-3 object-contain" />
                      <span className="text-sm font-medium text-[#010D2C]">Findings</span>
                    </div>
                    <div className="text-3xl font-bold text-gray-900 mb-1">{profile.stats?.totalFindings ?? 0}</div>
                    <div className="text-sm text-gray-500">Vulnerabilities Found</div>
                  </div>
                </div>

                {/* NOTE: SLA performance block was intentionally removed from the profile page
	                    for admin-facing views based on product feedback. SLA breach history is
	                    available in the admin dashboard instead. */}

                {/* Top Skills Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Skills</h3>
                  <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                    {profile.skills?.map((s: any) => (
                      <div key={s.name} className="space-y-2">
                        <div className="w-5/6 md:w-11/12">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-700">{formatSkillName(s.name)}</span>
                            <span className="text-sm text-gray-500">{s.percentage}%</span>
                          </div>
                          <div className="bg-[#58CC02]/[0.17] rounded-full h-2">
                            <div
                              className="bg-[#58CC02] h-2 rounded-full transition-all duration-300"
                              style={{ width: `${s.percentage}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Profile Card - Right Side (No margins, flush with container edge) */}
              <div className="w-[22rem] bg-[#010D2C] p-6 text-white">
                <div className="text-center">
                  <img
                    src={profile.profile?.avatarUrl || "/favicon.ico"}
                    alt="avatar"
                    className="w-32 h-32 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-sm"
                  />
                  <h3 className="text-2xl font-semibold mb-1">{profile.profile?.name || "Alex Morgan"}</h3>
                  <p className="text-[#86EFAC] text-lg mb-10">Pentester</p>

                  <div className="space-y-5 text-md pl-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-[#86EFAC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span className="text-white">Capture The Bug</span>
                    </div>

                    {profile.profile?.email && viewedUserId === currentUserId && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#86EFAC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="text-white">{profile.profile.email}</span>
                      </div>
                    )}

                    {profile.profile?.location && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#86EFAC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-white">{profile.profile.location}</span>
                      </div>
                    )}

                    {profile.profile?.joinedAt && (
                      <div className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-[#86EFAC]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-white">
                          Joined {new Date(profile.profile.joinedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex gap-8">
          {Tabs.map(t => (
            <button
              key={t}
              className={`pb-2 text-sm px-1 font-medium transition-colors ${tab === t
                ? "text-[#010D2C] border-b-2 border-[#010D2C]"
                : "text-[#6B7280] hover:text-gray-700"
                }`}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      {tab === "Overview" && (
        <div className="space-y-6">
          {/* Top Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Activity - Larger container */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                  <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="space-y-4">
                  {activity.slice(0, 3).map((it: any, index: number) => {
                    const isRetest = it.type === "retest";
                    const retestInfo = isRetest && typeof it.retest_id === "number" ? retestReportDetails[it.retest_id] : undefined;
                    const title = isRetest ? (retestInfo?.title || it.title) : (it.title);
                    const severity = isRetest ? (retestInfo?.severity || undefined) : (it.severity || it.severity_level || it.severityLabel);
                    const sevStyle = getSeverityStyle(severity);
                    const statusStyle = isRetest ? getRetestStatusStyle(it.status || it.tag) : undefined;
                    return (
                      <div key={it.id || index} className="flex items-start gap-3">
                        <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-900 truncate">{title || "Found SQL Injection vulnerability in payment system"}</span>
                            {severity && (
                              <span className={`px-2 py-0.5 ${sevStyle.bg} ${sevStyle.text} text-xs rounded-full`}>{sevStyle.label}</span>
                            )}
                          </div>
                          <div className="mt-1">
                            {isRetest ? (
                              <span className={`px-2 py-0.5 ${statusStyle?.bg} ${statusStyle?.text} text-xs rounded-full`}>
                                {`Retest: ${it.status || it.tag || ""}`.trim()}
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 bg-red-50 text-red-600 text-xs rounded-full">Vulnerability</span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 flex-shrink-0">
                          {it.date ? new Date(it.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : "Aug 15, 2023"}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setTab("Activity")}
                    className="text-green-600 text-sm font-medium hover:text-green-700"
                  >
                    View all activity
                  </button>
                </div>
              </div>
            </div>

            {/* Achievements - Smaller container */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Achievements</h3>
                  <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <div className="space-y-4">
                  {achievements.filter(a => a.unlocked).length === 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full border-2 border-gray-300 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">No Achievements Unlocked</h4>
                      <p className="text-sm text-gray-600">Start submitting reports to earn your first achievement!</p>
                    </div>
                  ) : (
                    achievements.filter(a => a.unlocked).slice(0, 3).map((a: any, index: number) => {
                      // Use the same icon function as in the achievements tab
                      const getAchievementIcon = (achievementName: string) => {
                        const name = achievementName?.toLowerCase() || '';
                        if (name.includes('report') || name.includes('geek')) {
                          return (
                            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 114 0 2 2 0 01-4 0zm2 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                            </svg>
                          );
                        } else if (name.includes('critical') || name.includes('hunter')) {
                          return (
                            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                            </svg>
                          );
                        } else if (name.includes('reliable') || name.includes('reporter')) {
                          return (
                            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          );
                        } else if (name.includes('dedicated') || name.includes('tester')) {
                          return (
                            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          );
                        } else if (name.includes('master')) {
                          return (
                            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15a1 1 0 100-2 1 1 0 000 2zm4 0a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                            </svg>
                          );
                        } else if (name.includes('multi') || name.includes('domain') || name.includes('expert')) {
                          return (
                            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                            </svg>
                          );
                        } else {
                          return (
                            <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                            </svg>
                          );
                        }
                      };

                      return (
                        <div key={a.key || index} className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full border-2 border-blue-500 flex items-center justify-center flex-shrink-0">
                            {getAchievementIcon(a.name)}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{a.name || "Fast Responder"}</div>
                            <div className="text-xs text-gray-600">{a.description || "Average response time under 2 hours"}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {a.unlocked ? (
                                a.earnedDate ? new Date(a.earnedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : null
                              ) : (
                                "Locked"
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setTab("Achievements")}
                    className="text-green-600 text-sm font-medium hover:text-green-700"
                  >
                    View all achievements
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Top Programs - Larger container */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Top Programs</h3>
                  <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                </div>
                <div className="space-y-0">
                  {(showAllPrograms ? profile?.topPrograms : profile?.topPrograms?.slice(0, 2))?.map((p: any, index: number) => (
                    <div key={p.programId || index}>
                      <div className="flex items-center justify-between py-3">
                        <h4 className="font-medium text-gray-900">{p.name || `Program ${p.programId}`}</h4>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${p.status === "Active" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                          {p.status || "Active"}
                        </span>
                      </div>
                      {(() => {
                        const rawDesc = (p as any)?.description ?? (p as any)?.program_description ?? (p as any)?.programDescription ?? (p as any)?.desc;
                        return rawDesc ? (
                          <p className="text-sm text-gray-600 mb-3">
                            {formatProgramDescription(rawDesc)}
                          </p>
                        ) : null;
                      })()}
                      {index < (((showAllPrograms ? profile?.topPrograms : profile?.topPrograms?.slice(0, 2))?.length) || 0) - 1 && (
                        <div className="border-b border-gray-200"></div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowAllPrograms(prev => !prev)}
                    className="text-green-600 text-sm font-medium hover:text-green-700"
                  >
                    {showAllPrograms ? "Show fewer" : "View all programs"}
                  </button>
                </div>
              </div>
            </div>

            {/* Certifications - Smaller container */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 h-full">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Certifications</h3>
                  <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="space-y-4">
                  {certs.slice(0, 2).map((c: any, index: number) => (
                    <div
                      key={c.certification_id || c.id || index}
                      className="rounded-xl bg-[#F9FAFB] p-4 flex items-center gap-4"
                    >
                      {/* Default certification icon (same as full Certifications tab) */}
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-blue-600 shadow-sm flex-shrink-0">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="8" r="4" />
                          <path d="M8 14l-2 6 6-3 6 3-2-6" />
                        </svg>
                      </div>

                      <div className="min-w-0">
                        <div className="text-sm md:text-base font-medium text-[#010D2C] leading-snug truncate">
                          {c.certification_name || "Certified Ethical Hacker (CEH)"}
                        </div>
                        <div className="mt-1 flex items-center text-xs md:text-xs text-gray-600 flex-wrap">
                          <span>{c.issuing_organization || "EC-Council"}</span>
                          <span className="mx-2 text-gray-400">•</span>
                          <span>
                            Issued {c.issue_date
                              ? new Date(c.issue_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                              : "May 2022"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setTab("Certifications")}
                    className="text-green-600 text-sm font-medium hover:text-green-700"
                  >
                    View all certifications
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {tab === "Achievements" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">All Achievements</h3>
            <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {achievements.map((a, index) => {
              // Different icons for each achievement
              const getAchievementIcon = (achievementName: string) => {
                const name = achievementName?.toLowerCase() || '';
                if (name.includes('report') || name.includes('geek')) {
                  return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm2 6a2 2 0 114 0 2 2 0 01-4 0zm2 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                  );
                } else if (name.includes('critical') || name.includes('hunter')) {
                  return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  );
                } else if (name.includes('reliable') || name.includes('reporter')) {
                  return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  );
                } else if (name.includes('dedicated') || name.includes('tester')) {
                  return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  );
                } else if (name.includes('master')) {
                  return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM8 15a1 1 0 100-2 1 1 0 000 2zm4 0a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                  );
                } else if (name.includes('multi') || name.includes('domain') || name.includes('expert')) {
                  return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                    </svg>
                  );
                } else {
                  // Default icon
                  return (
                    <svg className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                  );
                }
              };

              return (
                <div key={a.key || index} className="bg-gray-50 rounded-lg p-4 border border-gray-100 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-full border-2 border-blue-500 flex items-center justify-center flex-shrink-0">
                      {getAchievementIcon(a.name)}
                    </div>
                    {a.unlocked && (
                      <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        Earned
                      </span>
                    )}
                  </div>

                  <div className="mb-2">
                    <h4 className="text-sm font-semibold text-gray-900 mb-1">{a.name || "Achievement"}</h4>
                    <p className="text-xs text-gray-600 leading-relaxed">{a.description || "Achievement description"}</p>
                  </div>

                  {a.unlocked && (
                    <div className="text-xs text-gray-500">
                      Earned in {a.earnedDate ? new Date(a.earnedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : "Recently"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {tab === "Certifications" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Certifications & Education</h3>
          <h4 className="text-base font-medium text-gray-800 mb-4">Professional Certifications</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            {certs.map((c: any, index: number) => (
              <div key={c.certification_id || c.id || index} className="flex items-center justify-between rounded-xl p-5 bg-gray-50 shadow-sm hover:shadow-md transition-shadow max-w-[560px] w-full">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-lg bg-white border border-gray-200 flex items-center justify-center text-blue-600 shadow-sm">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M8 14l-2 6 6-3 6 3-2-6" />
                    </svg>
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{c.certification_name || "Certification"}</div>
                    <div className="text-sm text-gray-600">{c.issuing_organization || "Organization"}</div>
                    <div className="text-xs text-gray-500 mt-2">
                      Issued {c.issue_date ? new Date(c.issue_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' }) : "--"}
                      {c.expiry_date ? ` • Expires ${new Date(c.expiry_date).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}` : " • No Expiration"}
                    </div>
                  </div>
                </div>
                <div className="text-xs text-gray-400" />
              </div>
            ))}
          </div>
        </div>
      )}
      {tab === "Activity" && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Activity Timeline</h3>
            <svg className="w-5 h-5 text-[#58CC02]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>

          <div className="space-y-6">
            {(activityCollapsed ? activity.slice(0, 5) : activity).map((it: any, index: number) => {
              const isRetest = it.type === "retest";
              const retestInfo = isRetest && typeof it.retest_id === "number" ? retestReportDetails[it.retest_id] : undefined;
              const title = isRetest ? (retestInfo?.title || it.title) : (it.title);
              const severity = isRetest ? (retestInfo?.severity || undefined) : (it.severity);
              const sevStyle = getSeverityStyle(severity);
              const statusStyle = isRetest ? getRetestStatusStyle(it.status || it.tag) : undefined;
              return (
                <div key={it.id || index} className="relative flex items-start">
                  {/* Timeline dot with small line below */}
                  <div className="relative flex flex-col items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full flex-shrink-0"></div>
                    {/* Small line extending down from circle */}
                    <div className="w-0.5 h-6 bg-gray-200 mt-1"></div>
                  </div>

                  {/* Content */}
                  <div className="ml-4 flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-3">
                          <div className="text-sm font-medium text-gray-900">{title || "Activity item"}</div>
                          {severity && (
                            <span className={`px-2 py-1 text-xs rounded-full ${sevStyle.bg} ${sevStyle.text}`}>
                              {sevStyle.label}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {isRetest ? (
                            <span className={`px-2 py-1 ${statusStyle?.bg} ${statusStyle?.text} text-xs rounded-full`}>Retest: {it.status || it.tag}</span>
                          ) : (
                            <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Vulnerability</span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 ml-4 flex-shrink-0">
                        {it.date ? new Date(it.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ""}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 flex justify-center gap-4">
            {(activity.length >= 5 && (activityCollapsed || activityHasMore)) && (
              <button
                onClick={handleLoadMoreActivity}
                disabled={activityLoadingMore}
                className="px-4 py-2 text-sm font-medium text-green-600 hover:text-green-700 disabled:opacity-50"
              >
                {activityLoadingMore ? "Loading..." : "Show more"}
              </button>
            )}
            {!activityCollapsed && activity.length > 5 && (
              <button
                onClick={() => setActivityCollapsed(true)}
                className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                Show fewer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfilePage;


